// cloudFunctions/functions/schema/userSchema.ts
import { z as z2 } from "zod";

// cloudFunctions/functions/utils/timeHelpers.ts
import z from "zod";
var dateLike = z.union([
  z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  z.string().regex(/^\d+$/),
  z.number().min(1420066800),
  z.date()
]);
var timestampSchema = dateLike.transform((val) => {
  if (typeof val === "string") {
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
      return new Date(val).getTime() / 1e3;
    }
  }
  if (typeof val === "string" && /^\d+$/.test(val)) {
    val = Number.parseInt(val, 10);
  }
  if (typeof val === "number" && val >= 1420066800 && val <= 1e12) {
    return val;
  }
  if (typeof val === "number" && val > 1e12) {
    return Math.floor(val / 1e3);
  }
  if (val instanceof Date) {
    return Math.floor(new Date(val).getTime() / 1e3);
  }
  throw new Error("Invalid date");
});
var dateStringSchema = z.string().regex(/^\w{3}\s\d{1,2},\s\d{4}$/);

// cloudFunctions/functions/schema/userSchema.ts
var userSchemaFS = z2.object({
  dateCreated: z2.string().refine((v) => timestampSchema.parse(v)),
  age: z2.union([z2.boolean(), z2.number()]).optional(),
  allowWellness: z2.boolean().optional(),
  beyond_pulse_id: z2.union([z2.boolean(), z2.string().min(1)]).optional(),
  birthDate: z2.string().catch((_) => "N/A"),
  dateOfBirth: z2.string().nullish(),
  brand_ambassador: z2.boolean().optional(),
  cellPhone: z2.string().optional(),
  coachOrPlayer: z2.enum(["Player", "Coach", "Parent"]),
  email: z2.string().email(),
  bpEmail: z2.string().email().optional(),
  emergContact: z2.string().nullish(),
  emergContactCell: z2.string().nullish(),
  emergency_contact_full_name: z2.union([z2.boolean(), z2.string().min(1)]).optional(),
  emergency_contact_phone: z2.union([z2.boolean(), z2.string().min(1)]).optional(),
  emergency_email: z2.union([z2.boolean(), z2.string().email()]).optional(),
  firstName: z2.string().min(1),
  lastName: z2.string().min(1),
  freezeStreakCount: z2.number().optional(),
  gender: z2.string().nullish(),
  imgUrl: z2.string().url().catch(
    "https://firebasestorage.googleapis.com/v0/b/soccerfit-36cce.appspot.com/o/defaultProfileImg.png?alt=media&token=6ae0f86d-faf5-41e7-8551-1ce2efdb04c7"
  ),
  location: z2.union([z2.boolean(), z2.string().min(1)]).optional(),
  medical_state: z2.union([z2.boolean(), z2.string()]).optional(),
  payment_methods: z2.union([z2.boolean(), z2.record(z2.string(), z2.unknown())]).optional(),
  phone_number: z2.string().default("N/A"),
  // * only for players
  currentLevel: z2.number().default(0).optional(),
  currentStreak: z2.number().default(0).optional(),
  dominant_foot: z2.union([z2.boolean(), z2.string().min(1)]).optional(),
  doubleXPCount: z2.number().optional(),
  height: z2.number().optional().catch((_) => void 0),
  jerseyNumber: z2.coerce.number().optional(),
  position: z2.preprocess(
    (v) => String(v).toLowerCase(),
    z2.enum([
      "goalkeeper",
      "defender",
      "midfielder",
      "forward",
      "attackingmid",
      "centerback",
      "centerforward",
      "centralmid",
      "defensivemid",
      "fullback",
      "winger"
    ])
  ).optional().catch((_) => void 0),
  prefFoot: z2.string().optional(),
  pulsePoints: z2.number().optional(),
  saveStreakCount: z2.number().optional(),
  secondaryPosition: z2.string().default("None"),
  streakRecord: z2.number().optional(),
  totalXP: z2.number().optional(),
  activeInjuryKey: z2.string().min(1).optional(),
  weight: z2.number().optional().catch((_) => void 0),
  subscription_id: z2.union([z2.boolean(), z2.string().min(1)]).optional(),
  free_trial_ends_on: z2.union([
    z2.boolean(),
    z2.string().refine((v) => {
      if (typeof v === "string") {
        if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
          return v;
        }
      }
      return false;
    })
  ]).optional(),
  customer_id: z2.union([z2.boolean(), z2.string().min(1)]).optional(),
  payment_info: z2.object({
    autoRenew: z2.boolean(),
    balanceAfterCharge: z2.string().refine((v) => /\$\d+\.\d{2}/.test(v)).optional(),
    cancelsOn: z2.string().refine((v) => timestampSchema.parse(v)).optional(),
    currency: z2.string().refine((v) => /\w{3}/.test(v)).optional(),
    currentCreditBalance: z2.string().refine((v) => /\$\d+\.\d{2}/.test(v)).optional(),
    defaultSourceId: z2.string().min(1).optional(),
    interval: z2.string().min(1),
    method: z2.union([
      z2.object({
        brand: z2.string().min(1),
        expMonth: z2.number(),
        expYear: z2.number(),
        id: z2.string().min(1),
        last4: z2.string().refine((v) => /\d{4}/.test(v)),
        type: z2.string().min(1)
      }),
      z2.string()
    ]).optional(),
    nextAmountTotal: z2.string().refine((v) => /\$\d+\.\d{2}/.test(v)).optional(),
    nextDue: z2.string().optional(),
    owner: z2.object({ email: z2.string().email(), id: z2.string().min(1) }),
    recurrentAmount: z2.string().refine((v) => /\$\d+\.\d{2}/.test(v)).optional(),
    subscriptionId: z2.string().min(1)
  }).optional()
});
var userSchemaRtdb = userSchemaFS.pick({
  coachOrPlayer: true,
  dateCreated: true,
  email: true,
  firstName: true,
  imgUrl: true,
  lastName: true
});
export {
  userSchemaFS,
  userSchemaRtdb
};
//# sourceMappingURL=userSchema.js.map
